"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const University21Page = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-manrope">
        <section className="relative h-screen overflow-hidden">
          <div className="absolute inset-0 bg-gray-800 shimmer"></div>
          <div className="relative z-10 h-full flex items-center justify-center">
            <div className="text-center max-w-4xl mx-auto px-4">
              <div className="h-16 bg-gray-700 rounded mb-6 shimmer"></div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-700 rounded shimmer"></div>
                <div className="h-6 bg-gray-700 rounded w-4/5 mx-auto shimmer"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-manrope">

      {/* Hero Banner Section */}
      <section className="relative h-[45vh] sm:h-[60vh] md:h-[80vh] lg:h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('https://bic-bangkok.vercel.app/un.png')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent"></div>
        </div>

        <div className="relative z-10 h-full flex items-end pb-4 sm:pb-8 md:pb-14 lg:pb-18">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-2 bg-red-600/30 text-red-300 rounded-full text-sm font-medium mb-4 border border-red-500/30">
                University Pathway
              </span>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-6xl xl:text-7xl font-bold font-lora mb-3 sm:mb-6 text-brand-beige">
                University (2+1) Program
              </h1>
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl text-white leading-relaxed">
                Study in Bangkok. Graduate with a UK Degree.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is University 2+1 Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <span className="inline-block px-4 py-2 bg-red-600/20 text-red-400 rounded-full text-sm font-medium mb-4">
                  About the Program
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-lora mb-2 text-white">
                  What is the University (2+1) Program?
                </h2>
                <div className="w-20 h-1 bg-red-500 mt-4"></div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 mr-4">
                    <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    The University (2+1) Program at BI allows students to complete their entire university education in Bangkok while earning a UK degree. Students study for three years at BI Bangkok and graduate with a Bachelor's degree awarded by the <span className="text-red-400 font-semibold">University of Sunderland (UK)</span>.
                  </p>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 mr-4">
                    <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    This pathway provides students with a high-quality British university education in an international learning environment, while staying in a safe and supportive campus in Bangkok.
                  </p>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 mr-4">
                    <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    Students benefit from international faculty, modern facilities, and small class sizes, ensuring personalized academic support throughout their studies.
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  3 Years in Bangkok
                </span>
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  UK Bachelor's Degree
                </span>
                <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/30">
                  University of Sunderland
                </span>
              </div>
            </div>

            {/* Sunderland Logo Card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl p-8 h-full shadow-2xl border border-red-500/20">
                <div className="text-center mb-8">
                  <div className="bg-white rounded-xl p-6 mb-6 shadow-lg flex items-center justify-center" style={{ minHeight: '140px' }}>
                    <img
                      src="/widSunderland.jpg"
                      alt="University of Sunderland Logo"
                      className="max-h-24 w-auto mx-auto object-contain"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-red-400 mb-2 font-lora">
                    University of Sunderland
                  </h3>
                  <p className="text-gray-300">
                    Your degree, awarded by a recognised UK university
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">3</div>
                    <div className="text-white text-sm">Years Program</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">UK</div>
                    <div className="text-white text-sm">Accredited Degree</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">2</div>
                    <div className="text-white text-sm">Available Programs</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-center hover:bg-red-500/10 transition-colors duration-300 border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400 mb-1">BKK</div>
                    <div className="text-white text-sm">Study Location</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Programs Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-lora font-bold text-center mb-12 md:mb-20 text-brand-beige">
            Available Programs
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Business */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group border border-red-500/20 text-center">
              <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">💼</div>
              <h3 className="text-3xl font-bold text-brand-red mb-4 font-lora">Business</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                Develop essential business skills in management, marketing, finance, and entrepreneurship for a global career.
              </p>
              <div className="mt-6 pt-6 border-t border-red-500/20">
                <span className="px-4 py-2 bg-red-500/10 text-red-400 rounded-full text-sm border border-red-500/30">
                  Bachelor of Business
                </span>
              </div>
            </div>

            {/* Information Technology */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group border border-red-500/20 text-center">
              <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">💻</div>
              <h3 className="text-3xl font-bold text-brand-red mb-4 font-lora">Information Technology (IT)</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                Build expertise in software development, networks, cybersecurity, and modern digital systems.
              </p>
              <div className="mt-6 pt-6 border-t border-red-500/20">
                <span className="px-4 py-2 bg-red-500/10 text-red-400 rounded-full text-sm border border-red-500/30">
                  Bachelor of IT
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Structure Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              Program Structure
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Three years of study entirely in Bangkok — ending with a UK degree
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Timeline */}
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-red-500/40 hidden sm:block"></div>

              <div className="space-y-6">
                {/* Year 1 */}
                <div className="flex items-start gap-6 group">
                  <div className="flex-shrink-0 w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">Y1</span>
                  </div>
                  <div className="flex-1 bg-dark-blue rounded-2xl p-6 shadow-lg border border-red-500/20 group-hover:border-red-500/50 transition-all duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-beige font-lora">Year 1</h3>
                        <p className="text-red-400 font-semibold mt-1">1st Year Undergraduate</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">BI Bangkok</span>
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">On Campus</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Year 2 */}
                <div className="flex items-start gap-6 group">
                  <div className="flex-shrink-0 w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">Y2</span>
                  </div>
                  <div className="flex-1 bg-dark-blue rounded-2xl p-6 shadow-lg border border-red-500/20 group-hover:border-red-500/50 transition-all duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-beige font-lora">Year 2</h3>
                        <p className="text-red-400 font-semibold mt-1">2nd Year Undergraduate</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">BI Bangkok</span>
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">On Campus</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Year 3 */}
                <div className="flex items-start gap-6 group">
                  <div className="flex-shrink-0 w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">Y3</span>
                  </div>
                  <div className="flex-1 bg-dark-blue rounded-2xl p-6 shadow-lg border border-red-500/20 group-hover:border-red-500/50 transition-all duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-beige font-lora">Year 3</h3>
                        <p className="text-red-400 font-semibold mt-1">Final Year</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">BI Bangkok</span>
                        <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm border border-red-500/20">On Campus</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Graduation */}
                <div className="flex items-start gap-6 group">
                  <div className="flex-shrink-0 w-16 h-16 bg-brand-beige rounded-full flex items-center justify-center shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-black text-2xl">🎓</span>
                  </div>
                  <div className="flex-1 bg-gradient-to-r from-red-900/30 to-gray-900 rounded-2xl p-6 shadow-lg border border-brand-beige/30 group-hover:border-brand-beige/60 transition-all duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-brand-beige font-lora">Graduation</h3>
                        <p className="text-white font-semibold mt-1">Bachelor's Degree — University of Sunderland (UK)</p>
                      </div>
                      <span className="px-4 py-2 bg-brand-beige/10 text-brand-beige rounded-full text-sm border border-brand-beige/30 font-semibold">
                        🇬🇧 UK Degree
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-lora font-bold text-center mb-12 md:mb-20 text-brand-beige">
            Why Choose the UK Degree Pathway?
          </h2>

          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="space-y-6 md:space-y-8">
              <p className="text-lg md:text-xl leading-relaxed text-gray-200">
                <span className="text-brand-red font-semibold text-xl md:text-2xl">
                  The University (2+1) Program
                </span>{" "}
                offers a unique opportunity to earn a globally recognized British degree without the complexity or cost of relocating abroad.
              </p>

              <div className="bg-gradient-to-r from-black to-gray-900 p-6 md:p-8 border-l-4 border-brand-red shadow-xl rounded-lg">
                <h3 className="text-2xl md:text-3xl font-lora font-bold text-brand-beige mb-4 md:mb-6">
                  Key Advantages
                </h3>
                <ul className="space-y-3 md:space-y-4 text-base md:text-lg">
                  {[
                    "Earn a UK Bachelor's Degree",
                    "Study 3 years in Bangkok",
                    "International learning environment",
                    "Lower cost compared to studying in the UK",
                    "Supportive campus for international students",
                    "Clear pathway to a global career",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center group">
                      <div className="w-3 h-3 bg-brand-red rounded-full mr-4 group-hover:scale-125 transition-transform duration-300 flex-shrink-0"></div>
                      <span className="text-brand-red font-semibold group-hover:text-brand-beige transition-colors duration-300">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex justify-center mt-8 lg:mt-0">
              <div className="bg-white p-6 md:p-8 shadow-2xl hover:shadow-brand-red/20 transition-all duration-500 transform hover:scale-105 max-w-sm w-full rounded-xl flex items-center justify-center" style={{ minHeight: '200px' }}>
                <img
                  src="/sunderland.webp"
                  alt="University of Sunderland Logo"
                  className="max-h-36 w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-lora font-bold text-center text-brand-red mb-12 md:mb-20">
            Ideal For Students Who Want
          </h2>

          <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
            {[
              {
                icon: (
                  <svg className="w-10 h-10 md:w-12 md:h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                title: "No Visa Uncertainty",
                desc: "A UK degree without the stress of student visa applications or overseas relocation",
              },
              {
                icon: (
                  <svg className="w-10 h-10 md:w-12 md:h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "International Experience",
                desc: "An international university experience and curriculum, right here in Bangkok",
              },
              {
                icon: (
                  <svg className="w-10 h-10 md:w-12 md:h-12 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                title: "Affordable Global Career",
                desc: "A clear and affordable pathway to a globally recognised career with a UK Bachelor's degree",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-dark-blue p-6 sm:p-8 md:p-10 text-center border-2 border-brand-beige/30 hover:border-brand-beige transition-all duration-500 transform hover:-translate-y-4 hover:shadow-2xl hover:shadow-brand-beige/30 group rounded-lg"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 bg-brand-beige flex items-center justify-center mx-auto mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-500">
                  {item.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-brand-beige mb-4 md:mb-6 group-hover:text-white transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-white text-lg md:text-xl leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN US Section */}
      <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-dark-blue to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-lora mb-8 text-brand-beige">
              JOIN US TODAY
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Start your journey to a UK degree — without leaving Bangkok
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Apply Online",
                description: "Submit your application through our streamlined online portal",
                icon: "📝",
              },
              {
                step: "02",
                title: "Get Assessment",
                description: "Receive personalized guidance and academic assessment",
                icon: "🎯",
              },
              {
                step: "03",
                title: "Start Learning",
                description: "Begin your University (2+1) program with expert instructors and support",
                icon: "🚀",
              },
            ].map((step, index) => (
              <div
                key={index}
                className="relative bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-brand-red rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-brand-beige font-bold text-xl">{step.step}</span>
                </div>
                <div className="text-4xl mb-4 mt-4">{step.icon}</div>
                <h3 className="text-2xl font-bold text-brand-beige mb-4 group-hover:text-brand-red transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate("/enquire")}
              className="bg-brand-red hover:bg-red-700 text-white font-bold py-4 px-12 text-lg transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-xl rounded-lg"
            >
              START YOUR APPLICATION
            </button>
          </div>
        </div>
      </section>

      {/* Ready to Apply CTA */}
      <section className="relative py-20 md:py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-lora mb-8 text-brand-beige">
            Ready to Earn Your UK Degree in Bangkok?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12 max-w-4xl mx-auto leading-relaxed">
            Take the first step toward your British Bachelor's degree. Study in Bangkok, graduate with the world.
          </p>
          <button
            onClick={() => navigate("/enquire")}
            className="bg-brand-red hover:bg-red-700 text-white font-bold py-4 px-12 text-lg transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-xl rounded-lg"
          >
            ENROLL NOW
          </button>
        </div>
      </section>
    </div>
  );
};

export default University21Page;